
import React, { useState, useEffect, useRef } from 'react';
import { usePIM } from '../../context/PIMContext';
import { X, Save, RotateCcw } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { PrimaryButton } from '../ui/PrimaryButton';
import { GhostButton } from '../ui/GhostButton';

const ThemeModal = ({ isOpen, onClose }) => {
    const { settings, updateSettings } = usePIM();
    const { toast } = useToast();

    const [colors, setColors] = useState({
        theme_bg_main: '#030304',
        theme_bg_panel: '#080a0f',
        theme_bg_surface: '#0f121b',
        theme_text_primary: '#f1f5f9',
        theme_text_muted: '#64748b',
        theme_border: '#1e293b',
        theme_bg_content: '#030304',
    });

    // Track previous open state to detect fresh open events
    const wasOpenRef = useRef(isOpen);

    // Reset/Sync state when modal opens
    useEffect(() => {
        // If we just opened (false -> true transition) OR if settings changed while open
        // We generally only want to sync on OPEN to avoid overwriting user edits if settings change in bg
        // checking !wasOpenRef.current && isOpen detects the rising edge.
        if (isOpen && settings) {
            // Simply overwrite local state with current settings on open
            setColors(prev => ({
                ...prev,
                ...settings
            }));
        }
        wasOpenRef.current = isOpen;
        // removing 'settings' from deps to strictly sync on OPEN only, 
        // to prevent typing interruption if background refresh happens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setColors(prev => ({ ...prev, [name]: value }));
        // Live preview
        document.documentElement.style.setProperty(name.replace('theme_', '--color-').replace(/_/g, '-'), value);
    };

    const handleSave = async () => {
        await updateSettings(colors);
        onClose();
    };

    const PRESETS = {
        'Deep Blue (Default)': {
            theme_bg_main: '#162040',
            theme_bg_panel: '#080a0fd9',
            theme_bg_surface: '#0f121bd9',
            theme_text_primary: '#ffffff',
            theme_text_muted: '#64748b',
            theme_border: '#1e293b',
            theme_bg_content: '#030304',
            theme_bg_image: ''
        },
        'Classic (Stone)': {
            theme_bg_main: '#0c0a09',
            theme_bg_panel: '#1c1917',
            theme_bg_surface: '#292524',
            theme_text_primary: '#e7e5e4',
            theme_text_muted: '#78716c',
            theme_border: '#44403c',
            theme_bg_content: '#0c0a09',
            theme_bg_image: ''
        }
    };

    const handleApplyPreset = (presetName) => {
        const preset = PRESETS[presetName];
        setColors(preset);
        Object.entries(preset).forEach(([key, value]) => {
            document.documentElement.style.setProperty(key.replace('theme_', '--color-').replace(/_/g, '-'), value);
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-[var(--color-bg-panel)] rounded-lg shadow-2xl border border-[var(--color-border)] w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="flex justify-between items-center p-4 border-b border-[var(--color-border)] bg-[var(--color-bg-main)]">
                    <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
                        Theme Settings
                    </h3>
                    <button onClick={onClose} className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
                    <div className="flex gap-2 mb-4">
                        <GhostButton type="button" onClick={() => handleApplyPreset('Deep Blue (Default)')} className="flex-1 bg-blue-900/20 hover:bg-blue-900/40 border border-blue-800 text-blue-200">Load Deep Blue Preset</GhostButton>
                        <GhostButton type="button" onClick={() => handleApplyPreset('Classic (Stone)')} className="flex-1 bg-stone-800 hover:bg-stone-700 border border-stone-600 text-stone-300">Load Classic Preset</GhostButton>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                        <ColorInput label="Main Background" name="theme_bg_main" value={colors.theme_bg_main} onChange={handleChange} />
                        <ColorInput label="Panel Background" name="theme_bg_panel" value={colors.theme_bg_panel} onChange={handleChange} />
                        <ColorInput label="Surface Background" name="theme_bg_surface" value={colors.theme_bg_surface} onChange={handleChange} />
                        <ColorInput label="Content Background" name="theme_bg_content" value={colors.theme_bg_content} onChange={handleChange} />
                        <ColorInput label="Primary Text" name="theme_text_primary" value={colors.theme_text_primary} onChange={handleChange} />
                        <ColorInput label="Muted Text" name="theme_text_muted" value={colors.theme_text_muted} onChange={handleChange} />
                        <ColorInput label="Border Color" name="theme_border" value={colors.theme_border} onChange={handleChange} />
                    </div>

                    <div className="border-t border-[var(--color-border)] pt-4">
                        <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-2">Background Image</label>
                        <div className="flex items-center gap-4">
                            {colors.theme_bg_image ? (
                                <div className="relative group w-24 h-16 rounded overflow-hidden border border-[var(--color-border)]">
                                    <img src={colors.theme_bg_image} alt="Background" className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                                        <button
                                            onClick={() => setColors(prev => ({ ...prev, theme_bg_image: '' }))}
                                            className="text-white bg-red-600 rounded-full p-1"
                                        >
                                            <X size={12} />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-24 h-16 rounded border border-dashed border-[var(--color-border)] flex items-center justify-center text-[var(--color-text-muted)] text-xs">
                                    No Image
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={async (e) => {
                                    if (e.target.files && e.target.files[0]) {
                                        const file = e.target.files[0];
                                        const formData = new FormData();
                                        formData.append('image', file);

                                        try {
                                            const res = await fetch('/api/settings/background-image', {
                                                method: 'POST',
                                                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')} ` },
                                                body: formData
                                            });
                                            if (res.ok) {
                                                const data = await res.json();
                                                setColors(prev => ({ ...prev, theme_bg_image: data.url }));
                                                // Live preview
                                                document.documentElement.style.setProperty('--theme-bg-image', `url('${data.url}')`);
                                            } else {
                                                toast.error("Failed to upload image");
                                            }
                                        } catch (err) {
                                            console.error(err);
                                            toast.error("Upload error");
                                        }
                                    }
                                }}
                                className="text-sm text-[var(--color-text-muted)] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-stone-800 file:text-stone-300 hover:file:bg-stone-700"
                            />
                        </div>
                    </div>

                    <div className="pt-4 flex justify-between gap-3 border-t border-[var(--color-border)] mt-4">
                        <div className="flex-1"></div>
                        <div className="flex gap-2">
                            <GhostButton onClick={onClose}>Cancel</GhostButton>
                            <PrimaryButton onClick={handleSave}>
                                <Save size={16} className="mr-2" /> Save Theme
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ColorInput = ({ label, name, value, onChange }) => (
    <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-[var(--color-text-primary)]">{label}</label>
        <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-[var(--color-text-muted)]">{value}</span>
            <input
                type="color"
                name={name}
                value={value}
                onChange={onChange}
                className="w-8 h-8 rounded cursor-pointer border-none bg-transparent p-0"
            />
        </div>
    </div>
);

export default ThemeModal;
